/**
 * AdminDashboard Component
 * 
 * Main dashboard component for the V2 admin panel that acts as a router
 * to specific dashboard components based on the current route. This component
 * implements a clean separation of concerns where each data type has its own
 * dedicated dashboard component while sharing the same underlying layout system.
 * 
 * ## Architecture
 * This component serves as the entry point for all admin dashboard pages:
 * - **Companies Dashboard**: `/dashboard/customers` - Company management
 * - **Suppliers Dashboard**: `/dashboard/suppliers` - Supplier management
 * - **Products Dashboard**: `/dashboard/products` - Product management
 * - **Open RFQs Dashboard**: `/dashboard/rfqs/open` - Open RFQ management
 * - **Closed RFQs Dashboard**: `/dashboard/rfqs/closed` - Closed RFQ management
 * - **System Settings Dashboard**: `/dashboard/settings/system` - System settings management
 * - **Users Dashboard**: `/dashboard/users` - User account management (TODO)
 * 
 * ## Routing Pattern
 * Each route maps to a specific dashboard component that uses the generic
 * DataDashboard system for consistent UI and functionality.
 * 
 * ## Extensibility
 * Adding new dashboard pages is simple:
 * 1. Create new dashboard component (e.g., `OrdersDashboard`)
 * 2. Import and add route case in `renderDashboardContent()`
 * 3. Add navigation item to sidebar configuration
 * 
 * @component
 * @example
 * // Basic usage
 * <AdminDashboard 
 *   user={adminUser} 
 *   currentRoute="/dashboard/customers"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 * />
 * 
 * @example
 * // With custom className
 * <AdminDashboard 
 *   user={adminUser} 
 *   currentRoute="/dashboard/users"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 *   className="custom-dashboard"
 * />
 */

import React from 'react';
import CompaniesDashboard from '../CompaniesDashboard';
import SuppliersDashboard from '../SuppliersDashboard';
import ProductsDashboard from '../ProductsDashboard';
import ProductDetailsDashboard from '../ProductDetailsDashboard';
import RFQManualProductsDashboard from '../RFQManualProductsDashboard';
import AdminRFQDashboard from '../AdminRFQDashboard';
import RFQDetailDashboard from '../RFQDetailDashboard';
import SystemSettingsDashboard from '../SystemSettingsDashboard';
import InboxDashboard from '../InboxDashboard';
import AgenticDashboard from '../AgenticDashboard';
import RequestAuditLogsDashboard from '../RequestAuditLogsDashboard';
import { AdminUser } from '../../../../types/admin-v2';
import { getDefaultPageForRole } from '../../../../config/admin-v2-permissions';
import './AdminDashboard.scss';

interface AdminDashboardProps {
  /** Current admin user */
  user: AdminUser;
  /** Current route path */
  currentRoute?: string;
  /** Navigation handler */
  onNavigate?: (route: string) => void;
  /** Logout handler */
  onLogout?: () => void;
  /** Custom className */
  className?: string;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  user,
  currentRoute = '/dashboard/customers',
  onNavigate,
  onLogout,
  className = ''
}) => {
  // Get page info based on current route
  const pageInfo = getDefaultPageForRole(user.role);

  // Route to appropriate dashboard component
  const renderDashboardContent = () => {
    // Extract pathname from currentRoute (remove query params)
    const pathname = currentRoute.split('?')[0];

    if (currentRoute.startsWith('/dashboard/products/manual-products')) {
      return (
        <RFQManualProductsDashboard
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate || (() => { })}
          onLogout={onLogout || (() => { })}
          manualMode="global"
        />
      );
    }

    switch (pathname) {
      case '/dashboard/customers':
        return (
          <CompaniesDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      case '/dashboard/suppliers':
        return (
          <SuppliersDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      case '/dashboard/products':
        return (
          <ProductsDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      case '/dashboard/products/details':
        // Check if there's a companyId in the URL query params
        const urlParams = new URLSearchParams(window.location.search);
        const companyIdFromUrl = urlParams.get('companyId');

        return (
          <ProductDetailsDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
            companyId={companyIdFromUrl || undefined}
            layoutType="admin"
          />
        );

      case '/dashboard/rfqs/open':
        return (
          <AdminRFQDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
            phaseFilter="open"
          />
        );

      case '/dashboard/rfqs/finalized':
        return (
          <AdminRFQDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
            phaseFilter="finalized"
          />
        );

      case '/dashboard/inbox':
        return (
          <InboxDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      case '/dashboard/inbox/old':
        return (
          <InboxDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
            showDoneOnly
          />
        );

      case '/dashboard/agentic':
        return (
          <AgenticDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      case '/dashboard/request-audit-logs':
        return (
          <RequestAuditLogsDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      case '/dashboard/settings/system':
        return (
          <SystemSettingsDashboard
            user={user}
            currentRoute={currentRoute}
            onNavigate={onNavigate || (() => { })}
            onLogout={onLogout || (() => { })}
          />
        );

      // TODO: Add remaining dashboard components
      // case '/dashboard/users':
      //   return <UsersDashboard ... />;

      default:
        // Check if it's an RFQ detail route
        if (currentRoute.startsWith('/dashboard/rfqs/open/') && currentRoute !== '/dashboard/rfqs/open') {
          return (
            <RFQDetailDashboard
              user={user}
              currentRoute={currentRoute}
              onNavigate={onNavigate || (() => { })}
              onLogout={onLogout || (() => { })}
            />
          );
        }

        return (
          <div className="admin-dashboard-default">
            <h2>Welcome to Admin Dashboard</h2>
            <p>Select a section from the sidebar to get started.</p>
          </div>
        );
    }
  };

  return (
    <div className={`admin-dashboard ${className}`}>
      {renderDashboardContent()}
    </div>
  );
};

export default AdminDashboard;